import { LayerKey, type Tree } from '@festina-life/api'
import type { Optional } from '@festina-life/core'

const nullLayer = new LayerKey({ layerId: null, tenantId: null })

/**
 * Returns the LayerKey from `layers` that is highest up in the `layerTopology`.
 * Falls back to a null LayerKey when no topology or layers are provided.
 */
export const getHighestLayerKey = (
  layerTopology: Optional<Tree>,
  layers: LayerKey[],
): LayerKey => {
  if (!layerTopology || layers.length === 0) {
    return nullLayer
  }

  const depthMap = new Map<string, number>()
  const buildDepthMap = (node: Tree, depth: number) => {
    depthMap.set(node.name, depth)
    for (const child of node.children) {
      buildDepthMap(child, depth + 1)
    }
  }
  buildDepthMap(layerTopology, 0)

  const getLayerDepth = (layer: LayerKey): number => {
    const depthForLayer = layer.layerId ? (depthMap.get(layer.layerId) ?? 0) : 0

    return depthForLayer + (layer.tenantId ? 1 : 0)
  }

  let highestLayer = layers[0]
  let minDepth = highestLayer ? getLayerDepth(layers[0]) : 0

  for (const layer of layers.slice(1)) {
    const depth = getLayerDepth(layer)
    if (depth < minDepth) {
      minDepth = depth
      highestLayer = layer
    }
  }

  return highestLayer
}
